import * as React from 'react';
import { useObservable } from 'react-use-observable';

import { App, Button, Modal, Typography } from 'antd';

import { CHECKOUT_ELEMENTS_URL } from 'settings';
import type { ProductOffer } from 'v2/interfaces/product';
import eduzzAccountsService from 'v2/services/eduzzAccount';

import { Icon } from '@eduzz/ui-antd-theme';

import { PricesList } from './PricesList';

type LinksModalProps = {
  isAffiliate: boolean;
  isOpen: boolean;
  closeModal: () => void;
  offer: ProductOffer;
};

function LinksModal({ isAffiliate, isOpen, closeModal, offer }: LinksModalProps) {
  const { message } = App.useApp();
  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);

  function mountOfferLink(offer: ProductOffer) {
    const url = new URL(`${CHECKOUT_ELEMENTS_URL}/${offer.key}`);

    if (isAffiliate && user) {
      url.searchParams.append('a', String(user.id));
    }

    return String(url);
  }

  async function copyLinkToClipboard(link: string) {
    try {
      await navigator.clipboard.writeText(link);
      message.success('Link copiado para a área de transferência');
    } catch {
      message.error('Não foi possível copiar o link');
    }
  }

  const offerLink = mountOfferLink(offer);

  return (
    <Modal
      open={isOpen}
      onCancel={closeModal}
      title='Links da oferta'
      width={700}
      footer={
        <div className='flex justify-end'>
          <Button type='text' onClick={closeModal}>
            Fechar
          </Button>
        </div>
      }
    >
      <div className='flex flex-col gap-4'>
        <Typography.Text strong id='links-modal-offer-name'>
          {offer.name}
        </Typography.Text>

        <div className='flex flex-col gap-2 rounded-(--uizz-antd-border-radius-lg) bg-(--uizz-antd-color-fill-tertiary) p-4'>
          <Typography.Text type='secondary'>Página de pagamento (Eduzz Checkout Elements)</Typography.Text>
          <div className='flex items-center gap-2 rounded-(--uizz-antd-border-radius) border border-(--uizz-antd-color-border) bg-(--uizz-antd-color-bg-container) px-3 py-1.5'>
            <Icon.Link size={14} className='shrink-0 text-(--uizz-antd-color-text-secondary)' />
            <Typography.Link
              className='min-w-0 flex-1 truncate text-sm'
              id='links-modal-offer-link'
              href={offerLink}
              target='_blank'
              rel='noreferrer'
            >
              {offerLink}
            </Typography.Link>
            <Button
              type='text'
              size='small'
              shape='circle'
              id='links-modal-copy-button'
              aria-label='Copiar link'
              icon={<Icon.Copy size={14} />}
              onClick={() => copyLinkToClipboard(offerLink)}
            />
          </div>
        </div>

        {offer.prices?.length > 0 && <PricesList isAffiliate={isAffiliate} offer={offer} />}
      </div>
    </Modal>
  );
}

export { LinksModal };
